<script setup>
import { computed, ref, useAttrs } from 'vue';
import { useMapGetter, useStore } from 'dashboard/composables/store';
import { useBridgeOutboundCall } from 'dashboard/composables/useBridgeOutboundCall';

import Button from 'dashboard/components-next/button/Button.vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';

const props = defineProps({
  phone: { type: String, default: '' },
  contactId: { type: [String, Number], required: true },
  // When set and the conversation's inbox can place calls, the call goes out
  // through that inbox; otherwise the agent picks a voice inbox.
  conversationId: { type: [String, Number], default: null },
  label: { type: String, default: '' },
  icon: { type: [String, Object, Function], default: '' },
  size: { type: String, default: 'sm' },
  tooltipLabel: { type: String, default: '' },
});

defineOptions({ inheritAttrs: false });
const attrs = useAttrs();
const store = useStore();

const dialogRef = ref(null);

const inboxesList = useMapGetter('inboxes/getInboxes');
const {
  canUseInbox,
  voiceInboxes,
  isCallDisabled,
  isInitiatingCall,
  startCall,
} = useBridgeOutboundCall();

const conversationInbox = computed(() => {
  if (!props.conversationId) return null;
  const conversation = store.getters.getConversationById(props.conversationId);
  const inbox = (inboxesList.value || []).find(
    i => i.id === conversation?.inbox_id
  );
  return inbox && canUseInbox(inbox) ? inbox : null;
});

const shouldRender = computed(
  () =>
    (voiceInboxes.value.length > 0 || conversationInbox.value) && !!props.phone
);

const callThrough = (inbox, conversationId = null) =>
  startCall({
    inbox,
    phone: props.phone,
    contactId: props.contactId,
    conversationId,
  });

const onClick = async () => {
  if (conversationInbox.value) {
    await callThrough(conversationInbox.value, props.conversationId);
    return;
  }
  if (voiceInboxes.value.length > 1) {
    dialogRef.value?.open();
    return;
  }
  const [inbox] = voiceInboxes.value;
  await callThrough(inbox);
};

const onPickInbox = async inbox => {
  dialogRef.value?.close();
  await callThrough(inbox);
};
</script>

<template>
  <span class="contents">
    <Button
      v-if="shouldRender"
      v-tooltip.top-end="tooltipLabel || null"
      v-bind="attrs"
      :disabled="isCallDisabled"
      :is-loading="isInitiatingCall"
      :label="label"
      :icon="icon"
      :size="size"
      @click="onClick"
    />

    <Dialog
      v-if="shouldRender && voiceInboxes.length > 1"
      ref="dialogRef"
      :title="$t('CONTACT_PANEL.VOICE_INBOX_PICKER.TITLE')"
      show-cancel-button
      :show-confirm-button="false"
      width="md"
    >
      <div class="flex flex-col gap-2">
        <button
          v-for="inbox in voiceInboxes"
          :key="inbox.id"
          type="button"
          class="flex items-center justify-between w-full px-4 py-2 text-left rounded-lg hover:bg-n-alpha-2"
          @click="onPickInbox(inbox)"
        >
          <div class="flex items-center gap-2">
            <span class="i-ri-phone-fill text-n-slate-10" />
            <span class="text-sm text-n-slate-12">{{ inbox.name }}</span>
          </div>
          <span v-if="inbox.phone_number" class="text-xs text-n-slate-10">
            {{ inbox.phone_number }}
          </span>
        </button>
      </div>
    </Dialog>
  </span>
</template>
